<template>
  <Transition name="sticky-h-scroll-fade">
    <div
      v-if="visible"
      class="sticky-h-scroll-bar py-0 my-0"
      :class="[
        layoutCollapsedStyle?.value ? 'pr-3 pl-0' : 'px-4 sm:px-6 lg:px-8',
        { 'sticky-h-scroll-bar--above-pagination': bottomPaginationVisible }
      ]"
      role="scrollbar"
      aria-orientation="horizontal"
      :aria-valuenow="scrollLeft"
      :aria-valuemin="0"
      :aria-valuemax="maxScrollLeft"
      aria-label="Horizontal table scroll"
    >
      <div
        ref="trackRef"
        class="sticky-h-scroll-track"
        @click="onTrackClick"
      >
        <div
          ref="thumbRef"
          class="sticky-h-scroll-thumb"
          :style="thumbStyle"
          @mousedown="onThumbMouseDown"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue'
import { useStickyHorizontalScroll } from '@/composables/useStickyHorizontalScroll'
import { useBottomPagination } from '@/composables/useBottomPagination'

const sidebarCollapse = inject<{
  isCollapsed: { value: boolean }
  toggle: () => void
  showToggle: { value: boolean }
} | null>('sidebarCollapse', null)

const layoutCollapsedStyle = inject<{ value: boolean } | null>('layoutCollapsedStyle', null)
const isSidebarCollapsed = computed(() => sidebarCollapse?.isCollapsed?.value ?? false)

const {
  scrollLeft,
  scrollWidth,
  clientWidth,
  visible,
  setScrollLeft,
  handleWheel,
  shouldStickyBarClaimHorizontalWheel
} = useStickyHorizontalScroll()

const { visible: bottomPaginationVisible } = useBottomPagination()

const trackRef = ref<HTMLElement | null>(null)
const thumbRef = ref<HTMLElement | null>(null)

const maxScrollLeft = computed(() => Math.max(0, scrollWidth.value - clientWidth.value))

const thumbStyle = computed(() => {
  const sw = scrollWidth.value
  const cw = clientWidth.value
  const max = maxScrollLeft.value
  if (sw <= 0 || max <= 0) return { width: '0', left: '0' }
  const ratio = cw / sw
  const widthPct = Math.min(100, ratio * 100)
  const leftPct = max > 0 ? (scrollLeft.value / max) * (100 - widthPct) : 0
  return {
    width: `${widthPct}%`,
    left: `${leftPct}%`
  }
})

function onTrackClick(e: MouseEvent) {
  const track = trackRef.value
  if (!track || maxScrollLeft.value <= 0) return
  const rect = track.getBoundingClientRect()
  const x = e.clientX - rect.left
  const trackWidth = rect.width
  const thumbWidth = thumbRef.value?.getBoundingClientRect().width ?? 0
  const clickRatio = Math.max(0, Math.min(1, x / trackWidth))
  const newScrollLeft = clickRatio * maxScrollLeft.value
  setScrollLeft(newScrollLeft)
}

let isDragging = false
let dragOffsetX = 0

function onThumbMouseDown(e: MouseEvent) {
  e.preventDefault()
  const thumb = thumbRef.value
  if (!thumb) return
  isDragging = true
  const thumbRect = thumb.getBoundingClientRect()
  dragOffsetX = e.clientX - thumbRect.left
  document.addEventListener('mousemove', onDocMouseMove)
  document.addEventListener('mouseup', onDocMouseUp)
}

function onDocMouseMove(e: MouseEvent) {
  if (!isDragging) return
  const track = trackRef.value
  const thumb = thumbRef.value
  if (!track || !thumb || maxScrollLeft.value <= 0) return
  const trackRect = track.getBoundingClientRect()
  const thumbWidth = thumb.getBoundingClientRect().width
  const trackWidth = trackRect.width
  const thumbRange = Math.max(0, trackWidth - thumbWidth)
  let thumbLeftInTrack = e.clientX - trackRect.left - dragOffsetX
  thumbLeftInTrack = Math.max(0, Math.min(thumbRange, thumbLeftInTrack))
  const scrollRatio = thumbRange > 0 ? thumbLeftInTrack / thumbRange : 0
  setScrollLeft(scrollRatio * maxScrollLeft.value)
}

function onDocMouseUp() {
  isDragging = false
  document.removeEventListener('mousemove', onDocMouseMove)
  document.removeEventListener('mouseup', onDocMouseUp)
}

function onWheel(e: WheelEvent) {
  if (!e.shiftKey) return
  // Nested horizontal scrollports (cards, BaseScrollbar tables) must win over global sticky scroll
  if (!shouldStickyBarClaimHorizontalWheel(e)) return
  handleWheel(true, e.deltaX, e.deltaY)
  e.preventDefault()
}

onMounted(() => {
  window.addEventListener('wheel', onWheel, { passive: false, capture: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('wheel', onWheel, { capture: true })
  document.removeEventListener('mousemove', onDocMouseMove)
  document.removeEventListener('mouseup', onDocMouseUp)
})
</script>

<style scoped>
.sticky-h-scroll-bar {
  position: fixed;
  left: var(--bottom-bar-inset-left, 0);
  right: 0;
  bottom: 0;
  z-index: 19;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  transition: left 0.15s ease-out;
}

.sticky-h-scroll-bar.sticky-h-scroll-bar--above-pagination {
  bottom: var(--bottom-bar-height, 69px);
}

.sticky-h-scroll-track {
  height: 12px;
  border-radius: 6px;
  background: var(--color-surface-hover);
  cursor: pointer;
  position: relative;
}

.sticky-h-scroll-thumb {
  position: absolute;
  top: 2px;
  height: 8px;
  border-radius: 4px;
  background: var(--color-text-muted);
  cursor: grab;
  min-width: 24px;
}

.sticky-h-scroll-thumb:active {
  cursor: grabbing;
}

.sticky-h-scroll-fade-enter-from,
.sticky-h-scroll-fade-leave-to {
  opacity: 0;
}
</style>
